import React from "react"
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"
import { Trans, useTranslation } from "react-i18next"
import { Profile } from "../../reducers/profilesSlice"
import {
  DriverFields,
  fullPhoneNumber,
  type DriverFieldValues,
} from "./DriverFields"

// Two numbers are the same number however they were typed or stored.
const normalizePhone = (phone: string | undefined) =>
  (phone ?? "").replace(/[^\d]/g, "")

const driverName = (profile: Profile) =>
  profile.firstName && profile.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile.name || profile.jid

interface AddDriverStepProps {
  values: DriverFieldValues
  onChange: (values: DriverFieldValues) => void
  /** The drivers this account can see - what a new one is checked against. */
  drivers: Profile[]
  isAdding: boolean
  onPickExisting: (driverJid: string) => void
  onBack: () => void
  onSubmit: (e: React.FormEvent) => void
}

/**
 * The driver form, shown in place of the crew form rather than in a dialog of
 * its own: a second dialog leaves the one underneath inert (aria-hidden,
 * pointer-events: none) once it closes.
 */
export const AddDriverStep: React.FC<AddDriverStepProps> = ({
  values,
  onChange,
  drivers,
  isAdding,
  onPickExisting,
  onBack,
  onSubmit,
}) => {
  const { t } = useTranslation()
  // A driver is their phone number - the account is provisioned from it, and
  // the backend upserts on it. Someone else's number would rename them.
  const typedPhone = normalizePhone(fullPhoneNumber(values))
  const samePhone = values.phoneWithoutCode.trim()
    ? drivers.find((driver) => normalizePhone(driver.phone) === typedPhone)
    : undefined

  // Two people can share a name, so this only warns.
  const typedName = `${values.firstName} ${values.lastName}`
    .trim()
    .toLowerCase()
  const sameName = typedName
    ? drivers.find((driver) => driverName(driver).toLowerCase() === typedName)
    : undefined

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("fleet.addDriverStep.backToChat")}
            className="h-7 w-7"
            disabled={isAdding}
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {t("fleet.addDriverStep.title")}
        </DialogTitle>
        <DialogDescription>
          {t("fleet.addDriverStep.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="space-y-4">
          <DriverFields
            values={values}
            onChange={onChange}
            disabled={isAdding}
            required
            autoFocus
          />
          {samePhone ? (
            <p
              role="status"
              className="text-sm text-amber-600 dark:text-amber-500"
            >
              <Trans
                i18nKey="fleet.addDriverStep.samePhone"
                values={{ name: driverName(samePhone) }}
                components={{
                  pick: (
                    <button
                      type="button"
                      className="underline"
                      onClick={() => onPickExisting(samePhone.jid)}
                    />
                  ),
                }}
              />
            </p>
          ) : (
            sameName && (
              <p
                role="status"
                className="text-sm text-amber-600 dark:text-amber-500"
              >
                {t("fleet.addDriverStep.sameName", {
                  name: driverName(sameName),
                })}
              </p>
            )
          )}
        </div>

        <DialogFooter>
          <div className="flex w-full justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isAdding}
              onClick={onBack}
            >
              {t("common.back")}
            </Button>
            <Button
              type="submit"
              disabled={isAdding || !!samePhone}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isAdding
                ? t("fleet.addDriverStep.adding")
                : t("fleet.addDriverStep.add")}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </>
  )
}

export default AddDriverStep
